import React, { useState, useEffect, useRef } from 'react';
import DataTable from '../components/ResponsiveDataTable';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { openUrl } from '../utils/nativeFiles';
import useIsMobile from '../hooks/useIsMobile';
import {
  getGeneratedInvoices,
  getInvoiceDownloadUrl,
  getEwayBillDownloadUrl,
  generateEwayBill,
  generateDeliveryChallanEwayBill,
  uploadManualInvoice,
  uploadEwayBill,
  uploadInvoiceFile,
  getOrderDetail,
  getProducts,
  getWarehouses,
  approveInvoiceCancellation,
  rejectInvoiceCancellation
} from '../config/api';

const GeneratedInvoices = () => {
  const isMobile = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
  const isHubManager = currentUser.role === 'hubManager' || currentUser.access?.includes('hubManager');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountsAccess, setAccountsAccess] = useState(false);

  // Manual upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({ orderId: '' });
  const [invoiceType, setInvoiceType] = useState('invoice');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadEwayFile, setUploadEwayFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const ewayFileInputRef = useRef(null);

  // Upload invoice-file (for records created without a PDF) modal
  const [showInvoiceFileModal, setShowInvoiceFileModal] = useState(false);
  const [invoiceFileInvoiceId, setInvoiceFileInvoiceId] = useState(null);
  const [invoiceFileToUpload, setInvoiceFileToUpload] = useState(null);
  const [invoiceFileUploading, setInvoiceFileUploading] = useState(false);
  const invoiceFileRef = useRef(null);

  // Order fetch for manual upload
  const [fetchingOrder, setFetchingOrder] = useState(false);
  const [orderFetched, setOrderFetched] = useState(false);
  const [ledgerItems, setLedgerItems] = useState([]);
  const [billingName, setBillingName] = useState('');
  const [gstNumber, setGstNumber] = useState('');

  // Add item
  const [productOptions, setProductOptions] = useState([]);
  const [addItemType, setAddItemType] = useState('');
  const [addItemProduct, setAddItemProduct] = useState(null);
  const [addItemPrice, setAddItemPrice] = useState('');
  const [addItemQty, setAddItemQty] = useState('');
  const [addItemGst, setAddItemGst] = useState('');
  const [addItemTotal, setAddItemTotal] = useState('');

  // Upload e-way bill modal
  const [showEwayUploadModal, setShowEwayUploadModal] = useState(false);
  const [ewayUploadInvoiceId, setEwayUploadInvoiceId] = useState(null);
  const [ewayUploadFile, setEwayUploadFile] = useState(null);
  const [ewayUploading, setEwayUploading] = useState(false);
  const ewayUploadFileRef = useRef(null);

  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]); // array of { value, label }

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    if (userData.role === 'superAdmin' || userData.access?.includes('finance')) {
      setAccountsAccess(true);
    }
    loadWarehouses();
    loadInvoices();
  }, []);

  const loadWarehouses = async () => {
    try {
      const res = await getWarehouses();
      if (res && res.data) {
        setWarehouseOptions(res.data.map(w => ({ value: w.id.toString(), label: w.name })));
      }
    } catch (err) {
      console.error('Failed to load warehouses:', err);
    }
  };

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const res = await getGeneratedInvoices();
      setInvoices(res?.data || []);
    } catch (err) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    // Text search
    if (searchText) {
      const q = searchText.toLowerCase();
      const matchesText = (
        (inv.order_id || '').toLowerCase().includes(q) ||
        (inv.invoice_number || '').toLowerCase().includes(q) ||
        (inv.order?.user_name || '').toLowerCase().includes(q) ||
        (inv.order?.user_phone || '').toLowerCase().includes(q) ||
        (inv.generation_method || '').toLowerCase().includes(q)
      );
      if (!matchesText) return false;
    }
    // Date filter
    if (dateFrom || dateTo) {
      const invDate = inv.invoice_date || inv.created_at || inv.createdAt;
      if (!invDate) return false;
      const d = new Date(invDate);
      if (dateFrom && d < new Date(dateFrom)) return false;
      if (dateTo) {
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        if (d > end) return false;
      }
    }
    // Hub (warehouse) filter — keep invoices whose order belongs to any selected hub
    if (selectedWarehouses.length > 0) {
      const invWarehouseId = inv.order?.warehouse_id;
      if (invWarehouseId == null) return false;
      const selectedIds = selectedWarehouses.map(w => w.value);
      if (!selectedIds.includes(invWarehouseId.toString())) return false;
    }
    return true;
  });

  const handleFetchOrder = async () => {
    if (!uploadData.orderId.trim()) { toast.error('Enter Order ID first'); return; }
    setFetchingOrder(true);
    try {
      // Load products if not loaded
      if (productOptions.length === 0) {
        const prodRes = await getProducts();
        setProductOptions((prodRes.data || []).map(p => ({ value: p.id, label: `${p.skuCode} - ${p.name}`, skuCode: p.skuCode, name: p.name, gst_percent: p.gst_percent || 5 })));
      }

      const res = await getOrderDetail(uploadData.orderId.trim());
      const order = res?.data;
      if (!order || !order.id) { toast.error('Order not found'); return; }

      setBillingName(order.companyNameGST || order.customerName || '');
      setGstNumber(order.gstNumber || '');

      // Build ledger items from order
      const items = [];
      const billingNameStr = order.companyNameGST || order.customerName || '';
      const gstNumberStr = order.gstNumber || '';

      (order.items || []).forEach(item => {
        if (item.kitItems && item.kitItems.length > 0) {
          // Expand kit component items (for Standard Kits and Custom Kits)
          // GST is computed on FULL base price per component — discount is shown as separate row
          const gstPercent = parseFloat(item.gstPercent) || 5;

          item.kitItems.forEach(ki => {
            const unitPrice = parseFloat(ki.price) || 0;
            const qty = (parseFloat(ki.qty) || 1) * (item.quantity || 1);
            const totalBasePrice = unitPrice * qty;
            const gstAmt = parseFloat((totalBasePrice * gstPercent / 100).toFixed(2));

            items.push({
              line_type: 'SKU',
              sku_id: ki.skuCode || '',
              sku_name: ki.productName || '',
              billing_name: billingNameStr,
              gst_number: gstNumberStr,
              price: unitPrice,
              qty,
              invoice_value: totalBasePrice,
              gst_amount: gstAmt,
              total: parseFloat((totalBasePrice + gstAmt).toFixed(2))
            });
          });
        } else {
          // Simple product item
          const price = parseFloat(item.price) || 0;
          const qty = item.quantity || 0;
          const gstPercent = item.gstPercent || 5;
          const invoiceValue = price * qty;
          const gstAmt = parseFloat((invoiceValue * gstPercent / 100).toFixed(2));

          items.push({
            line_type: 'SKU',
            sku_id: item.skuCode || '',
            sku_name: item.name || '',
            billing_name: billingNameStr,
            gst_number: gstNumberStr,
            price,
            qty,
            invoice_value: invoiceValue,
            gst_amount: gstAmt,
            total: parseFloat((invoiceValue + gstAmt).toFixed(2))
          });
        }
      });

      // Delivery charges
      if (parseFloat(order.deliveryCharges) > 0) {
        const inclGST = parseFloat(order.deliveryCharges);
        const excl = parseFloat((inclGST / 1.18).toFixed(2));
        const gst = parseFloat((inclGST - excl).toFixed(2));
        items.push({ line_type: 'Transportation', sku_id: 'Transportation', sku_name: 'Transportation', billing_name: billingNameStr, gst_number: gstNumberStr, price: excl, qty: 1, invoice_value: excl, gst_amount: gst, total: inclGST });
      }

      // Handling charges
      if (parseFloat(order.handlingCharges) > 0) {
        const inclGST = parseFloat(order.handlingCharges);
        const excl = parseFloat((inclGST / 1.18).toFixed(2));
        const gst = parseFloat((inclGST - excl).toFixed(2));
        items.push({ line_type: 'Handling', sku_id: 'Handling', sku_name: 'Handling Charges', billing_name: billingNameStr, gst_number: gstNumberStr, price: excl, qty: 1, invoice_value: excl, gst_amount: gst, total: inclGST });
      }

      // Combined discount (coupon_discount + kit_discount in a SINGLE row)
      const couponDisc = parseFloat(order.couponDiscount) || 0;
      const kitDisc = parseFloat(order.kitDiscount) || 0;
      const totalDiscount = couponDisc + kitDisc;

      if (totalDiscount > 0) {
        items.push({
          line_type: 'Discount',
          sku_id: 'Discount',
          sku_name: 'Discount',
          billing_name: billingNameStr,
          gst_number: gstNumberStr,
          price: -totalDiscount,
          qty: 1,
          invoice_value: -totalDiscount,
          gst_amount: 0,
          total: -totalDiscount
        });
      }

      // Platform charges
      if (parseFloat(order.platformCharges) > 0) {
        const inclGST = parseFloat(order.platformCharges);
        const excl = parseFloat((inclGST / 1.18).toFixed(2));
        const gst = parseFloat((inclGST - excl).toFixed(2));
        items.push({ line_type: 'Platform', sku_id: 'Platform', sku_name: 'Platform Charges', billing_name: billingNameStr, gst_number: gstNumberStr, price: excl, qty: 1, invoice_value: excl, gst_amount: gst, total: inclGST });
      }

      // If the user already selected Credit Note before fetching, store the
      // built items as reverse entries: positive price, negative qty, and
      // negative derived amounts (invoice_value, gst, total).
      const finalBuilt = invoiceType === 'credit_note'
        ? items.map(it => ({
            ...it,
            price: Math.abs(parseFloat(it.price) || 0),
            qty: -Math.abs(parseFloat(it.qty) || 0),
            invoice_value: -Math.abs(parseFloat(it.invoice_value) || 0),
            gst_amount: -Math.abs(parseFloat(it.gst_amount) || 0),
            total: -Math.abs(parseFloat(it.total) || 0)
          }))
        : items;

      setLedgerItems(finalBuilt);
      setOrderFetched(true);
    } catch (err) {
      toast.error(err.message || 'Failed to fetch order');
    } finally {
      setFetchingOrder(false);
    }
  };

  const handleLedgerItemChange = (index, field, value) => {
    setLedgerItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const updated = { ...item, [field]: value };
      // Recompute invoice value (price * qty) and total when price or qty change
      if (field === 'price' || field === 'qty') {
        const price = parseFloat(updated.price) || 0;
        const qty = parseFloat(updated.qty) || 0;
        const invoiceValue = parseFloat((price * qty).toFixed(2));
        const gstAmt = parseFloat(updated.gst_amount) || 0;
        updated.invoice_value = invoiceValue;
        updated.total = parseFloat((invoiceValue + gstAmt).toFixed(2));
      }
      return updated;
    }));
  };

  const handleRemoveLedgerItem = (index) => {
    setLedgerItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddItem = () => {
    if (!addItemType) { toast.error('Select item type'); return; }

    let newItem = {};
    if (addItemType === 'SKU') {
      if (!addItemProduct) { toast.error('Select a product'); return; }
      if (!addItemPrice || !addItemQty) { toast.error('Price and Qty are required'); return; }
      const price = parseFloat(addItemPrice) || 0;
      const qty = parseFloat(addItemQty) || 0;
      const gstPercent = addItemProduct.gst_percent || 5;
      const invoiceValue = price * qty;
      const gstAmt = parseFloat((invoiceValue * gstPercent / 100).toFixed(2));
      newItem = {
        line_type: 'SKU',
        sku_id: addItemProduct.skuCode,
        sku_name: addItemProduct.name,
        price, qty,
        invoice_value: invoiceValue,
        gst_amount: gstAmt,
        total: parseFloat((invoiceValue + gstAmt).toFixed(2))
      };
    } else {
      // Handling / Transportation / Discount / Platform Charges
      if (!addItemPrice) { toast.error('Amount is required'); return; }
      const amount = parseFloat(addItemPrice) || 0;
      const gstAmt = parseFloat(addItemGst) || 0;
      const total = parseFloat(addItemTotal) || (addItemType === 'Discount' ? -amount : amount + gstAmt);
      newItem = {
        line_type: addItemType,
        sku_id: addItemType,
        sku_name: addItemType === 'Discount' ? 'Discount' : addItemType === 'Transportation' ? 'Transportation' : addItemType === 'Handling' ? 'Handling Charges' : 'Platform Charges',
        price: addItemType === 'Discount' ? -amount : amount,
        qty: 1,
        invoice_value: addItemType === 'Discount' ? -amount : amount,
        gst_amount: gstAmt,
        total: addItemType === 'Discount' ? -amount : total
      };
    }

    setLedgerItems(prev => [...prev, newItem]);
    // Reset add form
    setAddItemType('');
    setAddItemProduct(null);
    setAddItemPrice('');
    setAddItemQty('');
    setAddItemGst('');
    setAddItemTotal('');
  };

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setUploadData({ orderId: '' });
    setInvoiceType('invoice');
    setUploadFile(null);
    setUploadEwayFile(null);
    setLedgerItems([]);
    setOrderFetched(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (ewayFileInputRef.current) ewayFileInputRef.current.value = '';
  };

  // Switch document type. When moving to Credit Note, flip line-item money
  // fields to negative (reverse entry); moving back to Invoice restores them
  // to positive. Qty is left untouched. Items remain fully editable after.
  const handleTypeChange = (newType) => {
    setInvoiceType(newType);
    // Credit note sign convention: price stays POSITIVE, quantity goes NEGATIVE,
    // and the derived amounts (invoice_value, gst, total) stay NEGATIVE.
    // Switching away from credit note restores everything to positive.
    const isCN = newType === 'credit_note';
    const pos = (v) => Math.abs(parseFloat(v) || 0);
    const signed = (v) => (isCN ? -pos(v) : pos(v));
    setLedgerItems(prev => prev.map(item => ({
      ...item,
      price: pos(item.price),          // always positive
      qty: signed(item.qty),           // negative for credit note
      invoice_value: signed(item.invoice_value),
      gst_amount: signed(item.gst_amount),
      total: signed(item.total)
    })));
  };

  const handleUploadManual = async () => {
    // Invoice number is auto-generated on the server. Invoice file is required
    // for an invoice, optional for a credit note (checked below).
    if (!uploadData.orderId) {
      toast.error('Order ID is required');
      return;
    }
    if (invoiceType !== 'invoice' && invoiceType !== 'credit_note' && invoiceType !== 'hub_to_hub') {
      toast.error('Please select a valid document type');
      return;
    }
    // Invoice file is mandatory for an invoice; optional for credit note and hub to hub.
    if (invoiceType === 'invoice' && !uploadFile) {
      toast.error('Invoice file (PDF) is required for an invoice');
      return;
    }
    if (!ledgerItems.length) {
      toast.error('At least one line item is required');
      return;
    }
    if (!billingName.trim()) {
      toast.error('Billing name is required');
      return;
    }
    // Inject billing name and GST into each item before sending
    const finalItems = ledgerItems.map(item => ({
      ...item,
      billing_name: billingName.trim(),
      gst_number: gstNumber.trim().toUpperCase()
    }));
    setUploading(true);
    try {
      await uploadManualInvoice(uploadData.orderId, uploadFile, uploadEwayFile, finalItems, invoiceType);
      toast.success('Manual invoice uploaded successfully');
      resetUploadModal();
      loadInvoices();
    } catch (err) {
      toast.error(err.message || 'Failed to upload invoice');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadInvoiceFile = async () => {
    if (!invoiceFileToUpload) {
      toast.error('Please select an invoice file');
      return;
    }
    setInvoiceFileUploading(true);
    try {
      await uploadInvoiceFile(invoiceFileInvoiceId, invoiceFileToUpload);
      toast.success('Invoice file uploaded successfully');
      setShowInvoiceFileModal(false);
      setInvoiceFileToUpload(null);
      setInvoiceFileInvoiceId(null);
      if (invoiceFileRef.current) invoiceFileRef.current.value = '';
      loadInvoices();
    } catch (err) {
      toast.error(err.message || 'Failed to upload invoice file');
    } finally {
      setInvoiceFileUploading(false);
    }
  };

  const handleUploadEwayBill = async () => {
    if (!ewayUploadFile) {
      toast.error('Please select an e-way bill file');
      return;
    }
    setEwayUploading(true);
    try {
      await uploadEwayBill(ewayUploadInvoiceId, ewayUploadFile);
      toast.success('E-way bill uploaded successfully');
      setShowEwayUploadModal(false);
      setEwayUploadFile(null);
      setEwayUploadInvoiceId(null);
      if (ewayUploadFileRef.current) ewayUploadFileRef.current.value = '';
      loadInvoices();
    } catch (err) {
      toast.error(err.message || 'Failed to upload e-way bill');
    } finally {
      setEwayUploading(false);
    }
  };

  const handleViewInvoice = async (invoiceId) => {
    try {
      const res = await getInvoiceDownloadUrl(invoiceId);
      openUrl(res.data.url);
    } catch (err) {
      toast.error(err.message || 'Failed to get download URL');
    }
  };

  const handleViewEwayBill = async (invoiceId) => {
    try {
      const res = await getEwayBillDownloadUrl(invoiceId);
      openUrl(res.data.url);
    } catch (err) {
      toast.error(err.message || 'Failed to get e-way bill URL');
    }
  };

  // Shared e-way runner: calls `generate(distance, transporterName)` and, on the
  // two recoverable errors, prompts once and retries:
  //   - NEED_TRANSPORTER_NAME: ask for the transporter name (vehicle-less path).
  //   - distance unavailable:  ask for the distance in KM.
  const runEwayGeneration = async (generate) => {
    let distance = null;
    let transporterName = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await generate(distance, transporterName);
        toast.success('E-way bill generated successfully');
        loadInvoices();
        return;
      } catch (err) {
        const msg = (err?.message || '').toLowerCase();
        if (err?.code === 'NEED_TRANSPORTER_NAME' && !transporterName) {
          const name = window.prompt('Transporter name could not be fetched automatically. Please enter the transporter name:');
          if (!name || !name.trim()) return; // user cancelled
          transporterName = name.trim();
          continue;
        }
        if (msg.includes('distance') && !distance) {
          const d = window.prompt('Distance between pincodes is not available. Please enter distance in KM:');
          if (!d || !(+d > 0)) return; // user cancelled
          distance = +d;
          continue;
        }
        toast.error(err?.message || 'Failed to generate e-way bill');
        return;
      }
    }
  };

  const handleGenerateEwayBill = (invoiceId) =>
    runEwayGeneration((distance, transporterName) => generateEwayBill(invoiceId, distance, transporterName));

  // Delivery Challan e-way bill (intrastate hub-to-hub) via the GSTZen EWB API.
  const handleGenerateDcEwayBill = (invoiceId) =>
    runEwayGeneration((distance, transporterName) => generateDeliveryChallanEwayBill(invoiceId, distance, transporterName));

  const handleApproveCancellation = async (invoiceId) => {
    if (!window.confirm('Approve cancellation? This will call the GSTZen cancel API.')) return;
    try {
      await approveInvoiceCancellation(invoiceId);
      toast.success('Cancellation approved');
      loadInvoices();
    } catch (err) {
      toast.error(err.message || 'Failed to approve cancellation');
    }
  };

  const handleRejectCancellation = async (invoiceId) => {
    if (!window.confirm('Reject this cancellation request?')) return;
    try {
      await rejectInvoiceCancellation(invoiceId);
      toast.success('Cancellation rejected');
      loadInvoices();
    } catch (err) {
      toast.error(err.message || 'Failed to reject cancellation');
    }
  };

  const getCancellationStatusBadge = (status) => {
    const map = {
      none: { label: '—', color: '#6c757d' },
      requested: { label: 'Cancellation Requested', color: '#fd7e14' },
      approved: { label: 'Cancelled', color: '#dc3545' },
      rejected: { label: 'Cancellation Rejected', color: '#6c757d' }
    };
    const s = map[status] || map.none;
    return <span style={{ fontSize: '11px', color: s.color, fontWeight: '500' }}>{s.label}</span>;
  };

  const columns = [
    {
      name: 'Order ID',
      selector: row => row.order_id,
      sortable: true,
      width: '120px',
      cell: row => (
        <a href={`/orders/${row.order_id}`} target={isMobile ? undefined : '_blank'} rel="noreferrer"
          style={{ color: '#0d6efd', fontWeight: '600', fontSize: '12px' }}>
          {row.order_id}
        </a>
      )
    },
    {
      name: 'Invoice No.',
      selector: row => row.invoice_number,
      sortable: true,
      width: '140px',
      cell: row => (
        <span style={{ fontSize: '12px', fontWeight: '600' }}>
          {row.invoice_number}
          {row.type === 'credit_note' && (
            <span style={{ marginLeft: '4px', fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', backgroundColor: '#f8d7da', color: '#842029' }}>CN</span>
          )}
          {row.type === 'delivery_challan' && (
            <span style={{ marginLeft: '4px', fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', backgroundColor: '#e2e3ff', color: '#3730a3' }}>DC</span>
          )}
          {row.type === 'hub_to_hub' && (
            <span style={{ marginLeft: '4px', fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', backgroundColor: '#d1e7dd', color: '#0f5132' }}>H2H</span>
          )}
        </span>
      )
    },
    {
      name: 'Date',
      selector: row => row.invoice_date,
      sortable: true,
      width: '110px',
      cell: row => (
        <span style={{ fontSize: '12px' }}>
          {row.invoice_date ? new Date(row.invoice_date).toLocaleDateString('en-IN') : '—'}
        </span>
      )
    },
    {
      name: 'Method',
      selector: row => row.generation_method,
      sortable: true,
      width: '90px',
      cell: row => {
        // A Delivery Challan is generated by the system (order detail screen),
        // not manually uploaded — show "System" even though it has no IRN.
        const isSystem = row.type === 'delivery_challan';
        const label = row.generation_method === 'gstzen' ? 'GSTZen' : (isSystem ? 'System' : 'Manual');
        const bg = row.generation_method === 'gstzen' ? '#d1ecf1' : (isSystem ? '#e2e3ff' : '#d4edda');
        const fg = row.generation_method === 'gstzen' ? '#0c5460' : (isSystem ? '#3730a3' : '#155724');
        return (
          <span style={{
            fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '4px',
            backgroundColor: bg, color: fg
          }}>
            {label}
          </span>
        );
      }
    },
    {
      name: 'Hub',
      selector: row => row.order?.warehouse?.name || '',
      sortable: true,
      width: '130px',
      cell: row => (
        <span style={{ fontSize: '12px' }}>
          {row.order?.warehouse?.name || '—'}
        </span>
      )
    },
    {
      name: 'IRN',
      selector: row => row.gstzen_irn,
      width: '120px',
      cell: row => (
        <span style={{ fontSize: '11px', color: '#6c757d', fontFamily: 'monospace' }}>
          {row.gstzen_irn ? `${row.gstzen_irn.substring(0, 12)}...` : '—'}
        </span>
      )
    },
    {
      name: 'Cancellation',
      selector: row => row.cancellation_status,
      width: '180px',
      cell: row => getCancellationStatusBadge(row.cancellation_status)
    },
    {
      name: 'Created',
      selector: row => row.created_at || row.createdAt,
      sortable: true,
      width: '130px',
      cell: row => {
        const d = row.created_at || row.createdAt;
        return (
          <span style={{ fontSize: '11px', color: '#6c757d' }}>
            {d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
          </span>
        );
      }
    },
    {
      name: 'Actions',
      width: '220px',
      cell: row => (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {/* View invoice */}
          {row.file_path && (
            <button onClick={() => handleViewInvoice(row.id)}
              style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#0d6efd', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Invoice
            </button>
          )}

          {/* Upload Invoice file - when record has no invoice PDF yet (mirrors Upload E-Way) */}
          {!row.file_path && row.cancellation_status !== 'approved' && (
            <button onClick={() => { setInvoiceFileInvoiceId(row.id); setShowInvoiceFileModal(true); }}
              style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#0d6efd', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Upload Invoice
            </button>
          )}

          {/* View e-way bill - only for GSTZen invoices */}
          {row.generation_method === 'gstzen' && row.eway_bill_path && (
            <button onClick={() => handleViewEwayBill(row.id)}
              style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#6f42c1', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              E-Way Bill
            </button>
          )}

          {/* View e-way bill for manual invoices */}
          {row.generation_method === 'manual' && row.eway_bill_path && (
            <button onClick={() => handleViewEwayBill(row.id)}
              style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#6f42c1', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              E-Way Bill
            </button>
          )}

          {/* Generate E-Way Bill via GSTZen - only for GSTZen invoices without one */}
          {row.generation_method === 'gstzen' && !row.eway_bill_path && row.cancellation_status !== 'approved' && (
            <button onClick={() => handleGenerateEwayBill(row.id)}
              style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#fd7e14', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Gen E-Way
            </button>
          )}

          {/* Generate E-Way Bill for a Delivery Challan (intrastate hub-to-hub) */}
          {row.type === 'delivery_challan' && !row.eway_bill_path && row.cancellation_status !== 'approved' && (
            <button onClick={() => handleGenerateDcEwayBill(row.id)}
              style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#fd7e14', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Gen E-Way
            </button>
          )}

          {/* Upload E-Way Bill - for any invoice without one */}
          {!row.eway_bill_path && row.cancellation_status !== 'approved' && (
            <button onClick={() => { setEwayUploadInvoiceId(row.id); setShowEwayUploadModal(true); }}
              style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#20c997', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Upload E-Way
            </button>
          )}

          {/* Cancel button removed - cancellation is requested from Order Detail page only */}

          {/* Approve / Reject cancellation - finance users only */}
          {accountsAccess && row.cancellation_status === 'requested' && (
            <>
              <button onClick={() => handleApproveCancellation(row.id)}
                style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Approve
              </button>
              <button onClick={() => handleRejectCancellation(row.id)}
                style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Reject
              </button>
            </>
          )}
        </div>
      ),
      ignoreRowClick: true
    }
  ];

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Generated Invoices</h2>
          {!isHubManager && (
            <button onClick={() => setShowUploadModal(true)}
              className="btn btn-success" style={{ fontSize: '12px' }}>
              + Upload Manual Invoice
            </button>
          )}
        </div>

        {(() => {
          const hasFilters = !!(searchText || dateFrom || dateTo || selectedWarehouses.length);
          const activeCount = [searchText, dateFrom, dateTo].filter(Boolean).length + (selectedWarehouses.length ? 1 : 0);
          const fieldLabel = { fontSize: '11px', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.03em' };
          const selectStyles = {
            menuPortal: base => ({ ...base, zIndex: 9999 }),
            control: (base, state) => ({
              ...base, minHeight: '36px', fontSize: '13px', borderRadius: '8px',
              borderColor: state.isFocused ? '#0d6efd' : '#d1d5db',
              boxShadow: state.isFocused ? '0 0 0 3px rgba(13,110,253,0.12)' : 'none',
              '&:hover': { borderColor: '#9ca3af' },
            }),
            valueContainer: base => ({ ...base, padding: '2px 8px' }),
            multiValue: base => ({ ...base, backgroundColor: '#e7f1ff', borderRadius: '6px' }),
            multiValueLabel: base => ({ ...base, color: '#0d6efd', fontSize: '12px', fontWeight: 600 }),
            placeholder: base => ({ ...base, color: '#9ca3af', fontSize: '13px' }),
          };
          return (
            <div className={isMobile ? `filters-card${!filtersOpen ? ' mobile-collapsed' : ''}` : undefined} style={{
              marginBottom: '16px', padding: '14px 16px', background: '#f9fafb',
              border: '1px solid #e5e7eb', borderRadius: '10px',
              display: 'flex', gap: '14px', alignItems: 'flex-end', flexWrap: 'wrap'
            }}>
              {isMobile && (
                <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                    Showing <strong style={{ color: '#111827' }}>{filteredInvoices.length}</strong> of {invoices.length}{activeCount ? ` · ${activeCount} filter${activeCount > 1 ? 's' : ''}` : ''}
                  </span>
                  <button type="button" className="btn btn-secondary mobile-filters-toggle" onClick={() => setFiltersOpen((open) => !open)}>
                    {filtersOpen ? 'Hide filters' : 'Filters'}
                  </button>
                </div>
              )}
              <div style={{ flex: '1 1 240px', minWidth: '220px' }}>
                <label style={fieldLabel}>Search</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Order ID, Invoice No, Name, Phone…"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  style={{ width: '100%', fontSize: '13px', height: '36px', borderRadius: '8px' }}
                />
              </div>
              <div style={{ flex: '0 0 150px' }}>
                <label style={fieldLabel}>Date From</label>
                <input type="date" className="form-control" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ fontSize: '13px', height: '36px', borderRadius: '8px' }} />
              </div>
              <div style={{ flex: '0 0 150px' }}>
                <label style={fieldLabel}>Date To</label>
                <input type="date" className="form-control" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ fontSize: '13px', height: '36px', borderRadius: '8px' }} />
              </div>
              <div style={{ flex: '1 1 260px', minWidth: '240px' }}>
                <label style={fieldLabel}>Hub</label>
                <Select
                  isMulti
                  value={selectedWarehouses}
                  onChange={(opts) => setSelectedWarehouses(opts || [])}
                  options={warehouseOptions}
                  placeholder="All Hubs"
                  isClearable
                  closeMenuOnSelect={false}
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                />
              </div>
              {hasFilters && (
                <button
                  className="btn btn-light"
                  style={{ fontSize: '12px', fontWeight: 600, padding: '8px 14px', height: '36px', border: '1px solid #d1d5db', borderRadius: '8px', color: '#374151', background: '#fff' }}
                  onClick={() => { setSearchText(''); setDateFrom(''); setDateTo(''); setSelectedWarehouses([]); }}
                >
                  Clear{activeCount ? ` (${activeCount})` : ''}
                </button>
              )}
              <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap', paddingBottom: '8px' }}>
                Showing <strong style={{ color: '#111827' }}>{filteredInvoices.length}</strong> of {invoices.length}
              </div>
            </div>
          );
        })()}

        <DataTable
          columns={columns}
          data={filteredInvoices}
          progressPending={loading}
          pagination
          paginationPerPage={20}
          paginationRowsPerPageOptions={[10, 20, 50]}
          noDataComponent={
            <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
              No generated invoices found
            </div>
          }
          customStyles={{
            headRow: { style: { backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' } },
            headCells: { style: { fontWeight: '600', color: '#495057', fontSize: '12px' } },
            rows: { style: { '&:hover': { backgroundColor: '#f8f9fa' } } }
          }}
        />
      </div>

      {/* Manual Upload Modal */}
      {showUploadModal && !isHubManager && (
        <div onClick={() => { setShowUploadModal(false); setOrderFetched(false); setLedgerItems([]); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '10px', width: orderFetched ? 'min(800px, 95vw)' : 'min(420px, 95vw)', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Upload Manual Invoice</div>
              <button onClick={resetUploadModal}
                style={{ background: 'none', border: 'none', fontSize: isMobile ? '24px' : '18px', cursor: 'pointer', color: '#6b7280', ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}>×</button>
            </div>

            <div style={{ padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Order ID + Fetch */}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                    Order ID <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input type="text" value={uploadData.orderId}
                    onChange={e => { setUploadData(p => ({ ...p, orderId: e.target.value })); setOrderFetched(false); setLedgerItems([]); }}
                    placeholder="e.g. ZNX-ABC123" disabled={orderFetched}
                    style={{ width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }} />
                </div>
                <button onClick={handleFetchOrder} disabled={fetchingOrder || orderFetched}
                  style={{ fontSize: '12px', padding: '8px 14px', background: orderFetched ? '#28a745' : '#0d6efd', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {fetchingOrder ? 'Fetching...' : orderFetched ? '✓ Fetched' : 'Fetch Order'}
                </button>
              </div>

              {orderFetched && (
                <>
                  {/* Document Type — invoice number is now auto-generated */}
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                      Type <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <select value={invoiceType} onChange={e => handleTypeChange(e.target.value)}
                      style={{ width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }}>
                      <option value="invoice">Invoice</option>
                      <option value="credit_note">Credit Note</option>
                      <option value="hub_to_hub">Hub to Hub</option>
                    </select>
                  </div>

                  {/* Billing Name & GST Number */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                        Billing Name <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input type="text" value={billingName}
                        onChange={e => setBillingName(e.target.value)}
                        placeholder="Company / Customer name"
                        style={{ width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                        GST Number
                      </label>
                      <input type="text" value={gstNumber} autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                        onChange={e => setGstNumber(e.target.value)}
                        placeholder="e.g. 09AAACZ9608E1Z3"
                        style={{ width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box', textTransform: 'uppercase' }} />
                    </div>
                  </div>

                  {/* Line Items Table */}
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                      Line Items ({ledgerItems.length})
                    </label>
                    <div style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                      <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f9fafb' }}>
                            <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Type</th>
                            <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>SKU</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Price</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Qty</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Inv. Value</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>GST</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Total</th>
                            <th style={{ padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {ledgerItems.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '4px 8px' }}>{item.line_type}</td>
                              <td style={{ padding: '4px 8px' }}>
                                <input value={item.sku_name} onChange={e => handleLedgerItemChange(idx, 'sku_name', e.target.value)}
                                  style={{ width: '100%', fontSize: '11px', padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px' }} />
                              </td>
                              <td style={{ padding: '4px 8px' }}>
                                <input type="number" value={item.price} onChange={e => handleLedgerItemChange(idx, 'price', e.target.value)}
                                  style={{ width: '70px', fontSize: '11px', padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', textAlign: 'right' }} />
                              </td>
                              <td style={{ padding: '4px 8px' }}>
                                <input type="number" value={item.qty} onChange={e => handleLedgerItemChange(idx, 'qty', e.target.value)}
                                  style={{ width: '50px', fontSize: '11px', padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', textAlign: 'right' }} />
                              </td>
                              <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '11px' }}>
                                ₹{(parseFloat(item.invoice_value) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td style={{ padding: '4px 8px' }}>
                                <input type="number" value={item.gst_amount} onChange={e => handleLedgerItemChange(idx, 'gst_amount', e.target.value)}
                                  style={{ width: '70px', fontSize: '11px', padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', textAlign: 'right' }} />
                              </td>
                              <td style={{ padding: '4px 8px' }}>
                                <input type="number" value={item.total} onChange={e => handleLedgerItemChange(idx, 'total', e.target.value)}
                                  style={{ width: '80px', fontSize: '11px', padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', textAlign: 'right' }} />
                              </td>
                              <td style={{ padding: '4px 8px' }}>
                                <button onClick={() => handleRemoveLedgerItem(idx)}
                                  style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '14px' }}>×</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Files */}
                  {/* Add Item Section */}
                  <div style={{ padding: '12px', border: '1px dashed #d1d5db', borderRadius: '6px', background: '#fafbfc' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>+ Add Item</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div style={{ minWidth: '140px' }}>
                        <label style={{ fontSize: '10px', color: '#6b7280' }}>Type</label>
                        <select value={addItemType} onChange={e => { setAddItemType(e.target.value); setAddItemProduct(null); }}
                          style={{ width: '100%', fontSize: '12px', padding: '5px 6px', border: '1px solid #d1d5db', borderRadius: '4px' }}>
                          <option value="">Select type</option>
                          <option value="SKU">Product</option>
                          <option value="Transportation">Delivery</option>
                          <option value="Handling">Handling</option>
                          <option value="Discount">Discount</option>
                          <option value="Platform">Platform Charges</option>
                        </select>
                      </div>
                      {addItemType === 'SKU' && (
                        <div style={{ minWidth: '220px', flex: 1 }}>
                          <label style={{ fontSize: '10px', color: '#6b7280' }}>Product</label>
                          <Select
                            value={addItemProduct ? { value: addItemProduct.value, label: addItemProduct.label } : null}
                            onChange={opt => setAddItemProduct(opt ? productOptions.find(p => p.value === opt.value) : null)}
                            options={productOptions}
                            placeholder="Search product..."
                            isClearable
                            menuPortalTarget={document.body}
                            styles={{ control: (p) => ({ ...p, minHeight: '30px', fontSize: '12px' }), option: (p) => ({ ...p, fontSize: '12px' }), menuPortal: b => ({ ...b, zIndex: 9999 }) }}
                          />
                        </div>
                      )}
                      <div style={{ width: '80px' }}>
                        <label style={{ fontSize: '10px', color: '#6b7280' }}>{addItemType === 'Discount' ? 'Amount' : 'Price'}</label>
                        <input type="number" value={addItemPrice} onChange={e => setAddItemPrice(e.target.value)}
                          style={{ width: '100%', fontSize: '12px', padding: '5px 6px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                      </div>
                      {addItemType === 'SKU' && (
                        <div style={{ width: '60px' }}>
                          <label style={{ fontSize: '10px', color: '#6b7280' }}>Qty</label>
                          <input type="number" value={addItemQty} onChange={e => setAddItemQty(e.target.value)}
                            style={{ width: '100%', fontSize: '12px', padding: '5px 6px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                      )}
                      {addItemType && addItemType !== 'SKU' && addItemType !== 'Discount' && (
                        <div style={{ width: '70px' }}>
                          <label style={{ fontSize: '10px', color: '#6b7280' }}>GST</label>
                          <input type="number" value={addItemGst} onChange={e => setAddItemGst(e.target.value)}
                            style={{ width: '100%', fontSize: '12px', padding: '5px 6px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                      )}
                      {addItemType && addItemType !== 'SKU' && addItemType !== 'Discount' && (
                        <div style={{ width: '80px' }}>
                          <label style={{ fontSize: '10px', color: '#6b7280' }}>Total</label>
                          <input type="number" value={addItemTotal} onChange={e => setAddItemTotal(e.target.value)}
                            style={{ width: '100%', fontSize: '12px', padding: '5px 6px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                      )}
                      <button onClick={handleAddItem} disabled={!addItemType}
                        style={{ fontSize: '12px', padding: '5px 12px', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', height: '30px' }}>
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Files */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                        Invoice File (PDF) {invoiceType === 'invoice'
                          ? <span style={{ color: '#dc3545' }}>*</span>
                          : <span style={{ color: '#6c757d', fontWeight: '400' }}>optional</span>}
                      </label>
                      <input type="file" ref={fileInputRef} accept=".pdf"
                        onChange={e => setUploadFile(e.target.files[0])}
                        style={{ width: '100%', fontSize: '12px' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                        E-Way Bill (PDF) <span style={{ color: '#6c757d', fontWeight: '400' }}>optional</span>
                      </label>
                      <input type="file" ref={ewayFileInputRef} accept=".pdf"
                        onChange={e => setUploadEwayFile(e.target.files[0])}
                        style={{ width: '100%', fontSize: '12px' }} />
                    </div>
                  </div>
                </>
              )}
            </div>

            {orderFetched && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 18px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
                <button onClick={resetUploadModal} disabled={uploading}
                  style={{ fontSize: '13px', padding: '7px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}>
                  Cancel
                </button>
                <button onClick={handleUploadManual} disabled={uploading}
                  style={{ fontSize: '13px', padding: '7px 16px', background: '#28a745', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff', fontWeight: '600', opacity: uploading ? 0.7 : 1 }}>
                  {uploading ? 'Uploading...' : 'Upload Invoice'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload E-Way Bill Modal */}
      {showEwayUploadModal && (
        <div onClick={() => setShowEwayUploadModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '10px', width: 'min(380px, 95vw)', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Upload E-Way Bill</div>
              <button onClick={() => setShowEwayUploadModal(false)}
                style={{ background: 'none', border: 'none', fontSize: isMobile ? '24px' : '18px', cursor: 'pointer', color: '#6b7280', ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}>×</button>
            </div>

            <div style={{ padding: '20px 18px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                E-Way Bill File (PDF) <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input type="file" ref={ewayUploadFileRef} accept=".pdf"
                onChange={e => setEwayUploadFile(e.target.files[0])}
                style={{ width: '100%', fontSize: '13px' }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 18px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <button onClick={() => setShowEwayUploadModal(false)} disabled={ewayUploading}
                style={{ fontSize: '13px', padding: '7px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}>
                Cancel
              </button>
              <button onClick={handleUploadEwayBill} disabled={ewayUploading}
                style={{ fontSize: '13px', padding: '7px 16px', background: '#20c997', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff', fontWeight: '600', opacity: ewayUploading ? 0.7 : 1 }}>
                {ewayUploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Invoice File Modal (for records created without a PDF) */}
      {showInvoiceFileModal && (
        <div onClick={() => setShowInvoiceFileModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '10px', width: 'min(380px, 95vw)', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Upload Invoice File</div>
              <button onClick={() => setShowInvoiceFileModal(false)}
                style={{ background: 'none', border: 'none', fontSize: isMobile ? '24px' : '18px', cursor: 'pointer', color: '#6b7280', ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}>×</button>
            </div>

            <div style={{ padding: '20px 18px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                Invoice File (PDF) <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input type="file" ref={invoiceFileRef} accept=".pdf"
                onChange={e => setInvoiceFileToUpload(e.target.files[0])}
                style={{ width: '100%', fontSize: '13px' }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 18px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <button onClick={() => setShowInvoiceFileModal(false)} disabled={invoiceFileUploading}
                style={{ fontSize: '13px', padding: '7px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}>
                Cancel
              </button>
              <button onClick={handleUploadInvoiceFile} disabled={invoiceFileUploading}
                style={{ fontSize: '13px', padding: '7px 16px', background: '#0d6efd', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff', fontWeight: '600', opacity: invoiceFileUploading ? 0.7 : 1 }}>
                {invoiceFileUploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratedInvoices;
